class CartDrawer extends HTMLElement {
  // JP EDIT — persistent header links and pending focus belong to one live
  // drawer. Editor replacement must not retain the detached response owner.
  static jpOpenOwner = null;

  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.jpOverlayClickHandler = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.jpOverlayClickHandler);
    this.jpSectionLoadHandler = this.setHeaderCartIconAccessibility.bind(this);
    this.jpOpenToken = 0;
    this.jpCartTriggerBindings = new Map();
  }

  connectedCallback() {
    document.addEventListener('shopify:section:load', this.jpSectionLoadHandler);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.jpSectionLoadHandler);
    this.jpCartTriggerBindings.forEach((dispose) => dispose());
    this.close();
  }

  setHeaderCartIconAccessibility() {
    if (!this.isConnected) return;
    // JP EDIT: every branded cart affordance opens Dawn's accessible drawer;
    // its href remains a complete no-JS fallback.
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, [data-jp-cart-drawer-trigger]');
    this.jpCartTriggerBindings.forEach((dispose, link) => { if (!link.isConnected) dispose(); });
    cartLinks.forEach((cartLink) => {
      if (cartLink.__jpCartDrawerBound?.owner === this) return;
      cartLink.__jpCartDrawerBound?.dispose?.();
      const attributes = ['role', 'aria-haspopup'].map((name) => [name, cartLink.getAttribute(name)]);
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      const openDrawer = (event) => {
        if (!this.isConnected) return;
        event.preventDefault();
        let focusTarget = cartLink;
        if (cartLink.closest('[data-jp-layer="drawer"]')) {
          focusTarget = document.querySelector('[data-jp-open="drawer"]') || cartLink;
          window.JapanScissors?.layers?.close('drawer');
        }
        this.open(focusTarget);
      };
      const onKeydown = (event) => {
        if (event.code?.toUpperCase() === 'SPACE' || event.key === ' ') openDrawer(event);
      };
      const binding = { owner: this, dispose: () => {
        cartLink.removeEventListener('click', openDrawer);
        cartLink.removeEventListener('keydown', onKeydown);
        if (cartLink.__jpCartDrawerBound === binding) {
          attributes.forEach(([name, value]) => {
            if (value === null) cartLink.removeAttribute(name);
            else cartLink.setAttribute(name, value);
          });
          delete cartLink.__jpCartDrawerBound;
        }
        this.jpCartTriggerBindings.delete(cartLink);
      } };
      cartLink.__jpCartDrawerBound = binding;
      this.jpCartTriggerBindings.set(cartLink, binding.dispose);
      cartLink.addEventListener('click', openDrawer);
      cartLink.addEventListener('keydown', onKeydown);
    });
  }

  open(triggeredBy) {
    if (!this.isConnected) return;
    if (CartDrawer.jpOpenOwner && CartDrawer.jpOpenOwner !== this) CartDrawer.jpOpenOwner.close(false);
    CartDrawer.jpOpenOwner = this;
    this.jpOwnsBodyLock ||= !document.body.classList.contains('overflow-hidden');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const wasActive = this.classList.contains('active');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.cancelPendingOpen();
    const token = this.jpOpenToken;
    const inner = this.querySelector('.drawer__inner');

    let focusTrapActivated = false;
    const activateFocusTrap = () => {
      if (focusTrapActivated || token !== this.jpOpenToken || !this.isConnected || CartDrawer.jpOpenOwner !== this || !this.classList.contains('active')) return;
      /* Trap the actual dialog in both empty and populated states. The former
         empty-state container was a child of the focus target, so reduced-
         motion browsers could install a trap while leaving focus on the
         opener outside it. Prefer the close control, with the focusable dialog
         itself as a resilient fallback. */
      const containerToTrapFocusOn = this.querySelector('.drawer__inner');
      const focusElement = containerToTrapFocusOn?.querySelector('.drawer__close') || containerToTrapFocusOn;
      if (!containerToTrapFocusOn || !focusElement) return;
      trapFocus(containerToTrapFocusOn, focusElement);
      /* Do not consume the transition fallback until focus really entered the
         dialog. Immediately after the active class is applied, some mobile
         browsers still regard the transitioning drawer as non-focusable. */
      if (document.activeElement !== containerToTrapFocusOn && !containerToTrapFocusOn.contains(document.activeElement)) {
        return;
      }
      focusTrapActivated = true;
      if (this.jpTransitionElement && this.jpTransitionHandler) {
        this.jpTransitionElement.removeEventListener('transitionend', this.jpTransitionHandler);
      }
      window.clearTimeout(this.jpFocusTrapTimer);
    };
    const activateFocusTrapAfterLayout = () => {
      this.jpFocusFrame = requestAnimationFrame(() => {
        if (token !== this.jpOpenToken || !this.isConnected) return;
        this.jpFocusFrame = requestAnimationFrame(activateFocusTrap);
      });
    };

    this.jpTransitionElement = inner;
    this.jpTransitionHandler = (event) => {
      if (event.target === inner && event.propertyName === 'transform') activateFocusTrap();
    };
    inner?.addEventListener('transitionend', this.jpTransitionHandler);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.jpOpenTimer = setTimeout(() => {
      if (token !== this.jpOpenToken || !this.isConnected) return;
      this.classList.add('animate', 'active');
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (wasActive || reducedMotion) activateFocusTrapAfterLayout();
      this.jpFocusTrapTimer = window.setTimeout(() => {
        activateFocusTrap();
        if (!focusTrapActivated) activateFocusTrapAfterLayout();
      }, 450);
    });

    document.body.classList.add('overflow-hidden');
  }

  cancelPendingOpen() {
    this.jpOpenToken += 1;
    window.clearTimeout(this.jpOpenTimer);
    window.clearTimeout(this.jpRenderTimer);
    window.clearTimeout(this.jpFocusTrapTimer);
    cancelAnimationFrame(this.jpFocusFrame);
    if (this.jpTransitionElement && this.jpTransitionHandler) {
      this.jpTransitionElement.removeEventListener('transitionend', this.jpTransitionHandler);
    }
  }

  close(restoreFocus = true) {
    this.cancelPendingOpen();
    this.classList.remove('active');
    if (CartDrawer.jpOpenOwner !== this) return;
    CartDrawer.jpOpenOwner = null;
    const opener = restoreFocus && this.activeElement?.isConnected !== false ? this.activeElement : null;
    removeTrapFocus(opener || null);
    if (this.jpOwnsBodyLock) document.body.classList.remove('overflow-hidden');
    this.jpOwnsBodyLock = false;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.replaceRenderedSections(parsedState, true);
  }

  reconcileContents(parsedState) {
    this.replaceRenderedSections(parsedState, false);
  }

  replaceRenderedSections(parsedState, shouldOpen) {
    /* Product forms can retain this instance across an editor replacement.
       Accepted cart sections must reach the live owner (including its empty
       state/focus), while a removed drawer must not write into a cart page. */
    if (!this.isConnected) {
      const current = document.querySelector('cart-drawer');
      if (current && current !== this && current.isConnected) {
        if (shouldOpen && !current.activeElement?.isConnected && this.activeElement?.isConnected) {
          current.setActiveElement(this.activeElement);
        }
        return current.replaceRenderedSections(parsedState, shouldOpen);
      }
      return;
    }
    this.productId = parsedState.id;
    const drawerMarkup = parsedState.sections?.['cart-drawer'];
    const sourceDrawer = drawerMarkup
      ? new DOMParser().parseFromString(drawerMarkup, 'text/html').querySelector('cart-drawer')
      : null;
    if (sourceDrawer) {
      this.classList.toggle(
        'is-empty',
        sourceDrawer.classList.contains('is-empty') || Boolean(sourceDrawer.querySelector('.drawer__inner-empty'))
      );
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const replacement = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);
      if (replacement !== null) sectionElement.innerHTML = replacement;
    });

    window.clearTimeout(this.jpRenderTimer);
    this.jpRenderTimer = setTimeout(() => {
      if (!this.isConnected) return;
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.jpOverlayClickHandler);
      if (shouldOpen) this.open();
    });
    document.dispatchEvent(new CustomEvent('jp:cart-dom-updated', {
      detail: { source: shouldOpen ? 'product-form' : 'product-form-error-reconcile' },
    }));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return source ? source.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
